import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../components/ui/Toast';
import api from '../../utils/api';
import { getImageUrl } from '../../utils/config';

const StoreList = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const response = await api.get('/stores');
      // Handle Laravel API response format
      if (response.data.success) {
        setStores(response.data.data || []);
      } else {
        showToast(response.data.message || 'Failed to load stores', 'error');
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load stores';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStore = async (storeId) => {
    if (!window.confirm('Are you sure you want to delete this store? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await api.delete(`/stores/${storeId}`);
      if (response.data.success) {
        setStores(stores.filter(store => store.id !== storeId));
        showToast(response.data.message || 'Store deleted successfully', 'success');
      } else {
        showToast(response.data.message || 'Failed to delete store', 'error');
      }
    } catch (error) {
      console.error('Error deleting store:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete store';
      showToast(errorMessage, 'error');
    }
  };

  const handleToggleStatus = async (storeId) => {
    try {
      const response = await api.patch(`/stores/${storeId}/toggle-status`);
      if (response.data.success) {
        setStores((prevStores) =>
          prevStores.map((store) =>
            store.id === storeId ? { ...store, active: !store.active } : store
          )
        );
        showToast(response.data.message || 'Store status updated', 'success');
      } else {
        showToast(response.data.message || 'Failed to update status', 'error');
      }
    } catch (error) {
      console.error('Error toggling store status:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update status';
      showToast(errorMessage, 'error');
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading stores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Store Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage all stores in your multi-store platform
          </p>
        </div>
        <Link
          to="/admin/stores/create"
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          <span className="mr-2">+</span>
          Add New Store
        </Link>
      </div>

      {/* Stores Grid */}
      {stores.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🏪</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No stores found</h3>
          <p className="text-gray-500 mb-6">Get started by creating your first store.</p>
          <Link
            to="/admin/stores/create"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <span className="mr-2">+</span>
            Create First Store
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {stores.map((store) => (
            <div key={store.id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              {/* Store Header */}
              <div className="p-6">
                <div className="flex items-start space-x-4">
                  {/* Store Icon */}
                  <div className="flex-shrink-0">
                    {store.icon ? (
                      <img
                        src={getImageUrl(store.icon)}
                        alt={`${store.name} icon`}
                        className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                        onError={e => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.parentNode.innerHTML = `<svg xmlns='http://www.w3.org/2000/svg' class='w-12 h-12 text-gray-400' fill='none' viewBox='0 0 24 24' stroke='currentColor'><rect width='100%' height='100%' rx='8' fill='#f3f4f6'/><path stroke='#9ca3af' strokeWidth='2' d='M8 16h8M8 12h8m-8-4h8'/></svg>`;
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <span className="text-gray-400 text-xl">🏪</span>
                      </div>
                    )}
                  </div>

                  {/* Store Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {store.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">{store.email}</p>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="mt-4 space-y-2">
                  {store.phone && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="mr-2">📞</span>
                      <span>{store.phone}</span>
                    </div>
                  )}
                  {store.contact_email && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="mr-2">✉️</span>
                      <span>{store.contact_email}</span>
                    </div>
                  )}
                  {store.location && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="mr-2">📍</span>
                      <span className="truncate">{store.location}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 pb-6">
                <div className="flex flex-col sm:flex-row gap-2">
                  <Link
                    to={`/admin/stores/${store.slug}/edit`}
                    className="flex-1 px-3 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors text-center"
                  >
                    Edit
                  </Link>
                  <Link
                    to={`/admin/stores/${store.slug}`}
                    className="flex-1 px-3 py-2 text-sm text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition-colors text-center"
                  >
                    Manage Store
                  </Link>
                  <button
                    onClick={() => handleDeleteStore(store.id)}
                    className="flex-1 px-3 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => handleToggleStatus(store.id)}
                    className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors text-center ${store.active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    {store.active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={closeToast}
      />
    </div>
  );
};

export default StoreList; 